import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Footer } from "../components/footer";
import CropPhenologyTimeline from "../components/crops/CropPhenologyTimeline";
import CropStoryCard from "../components/crops/CropStoryCard";
import { cropText } from "../components/crops/cropText";
import useDocumentMeta from "../hooks/useDocumentMeta";
import cropsData from "../data/crops.json";

/**
 * /wiki        — index of public crop reference pages
 * /wiki/:crop  — public reference data for one crop (climate / soil / water /
 *                fertilizer / phenology / cited references)
 *
 * Distinct purpose from /crops/:slug: that is a marketing story page, this is
 * the raw, citable reference data. The two cross-link.
 */

function WikiRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-800 text-right">{value}</span>
    </div>
  );
}

function WikiCard({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export default function CropWiki({ lang = "EN", toggleLanguage }) {
  const isAr = lang === "AR";
  const { crop: slug } = useParams();
  const crop = slug ? cropsData.crops.find((c) => c.slug === slug) : null;

  useDocumentMeta({
    title: crop
      ? `${cropText(crop.name, lang)} — ${isAr ? "موسوعة المحاصيل" : "Crop Wiki"} — FarmTopia`
      : isAr
      ? "موسوعة المحاصيل المصرية — فارمتوبيا"
      : "Egyptian Crop Wiki — FarmTopia",
    description: crop
      ? isAr
        ? `بيانات مرجعية عن ${cropText(crop.name, lang)}: المناخ والتربة والمياه والتسميد ومراحل النمو.`
        : `Reference data for ${cropText(crop.name, lang)}: climate, soil, water, fertilizer and phenology.`
      : isAr
      ? "موسوعة مرجعية للمحاصيل المصرية: بيانات المناخ والتربة والمياه والتسميد."
      : "A reference wiki of Egyptian crops: climate, soil, water and fertilizer data.",
  });

  /* ---- Index view (/wiki) ---- */
  if (!slug) {
    return (
      <div dir={isAr ? "rtl" : "ltr"} className="bg-gray-50 min-h-screen">
        <Navbar lang={lang} toggleLanguage={toggleLanguage} />
        <main className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-900">
            {isAr ? "موسوعة المحاصيل المصرية" : "Egyptian Crop Wiki"}
          </h1>
          <p className="mt-3 text-gray-600 max-w-2xl">
            {isAr
              ? "بيانات مرجعية لكل محصول: المناخ والتربة والمياه والتسميد ومراحل النمو ومعاملات المحصول، مع المراجع العلمية."
              : "Reference data for each crop — climate, soil, water, fertilizer, phenology and crop coefficients, with cited scientific references."}
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cropsData.crops.map((c) => (
              <CropStoryCard key={c.slug} crop={c} lang={lang} to={`/wiki/${c.slug}`} />
            ))}
          </div>
        </main>
        <Footer lang={lang} />
      </div>
    );
  }

  /* ---- Not found ---- */
  if (!crop) {
    return (
      <div dir={isAr ? "rtl" : "ltr"} className="bg-gray-50 min-h-screen">
        <Navbar lang={lang} toggleLanguage={toggleLanguage} />
        <main className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {isAr ? "لم نجد هذا المحصول" : "Crop not found"}
          </h1>
          <Link to="/wiki" className="mt-4 inline-block text-emerald-700 font-medium">
            {isAr ? "تصفّح الموسوعة ←" : "← Browse the wiki"}
          </Link>
        </main>
        <Footer lang={lang} />
      </div>
    );
  }

  /* ---- Detail view (/wiki/:crop) ---- */
  const { wiki } = crop;
  return (
    <div dir={isAr ? "rtl" : "ltr"} className="bg-gray-50 min-h-screen">
      <Navbar lang={lang} toggleLanguage={toggleLanguage} />
      <main className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <Link to="/wiki" className="text-sm text-emerald-700">
              {isAr ? "موسوعة المحاصيل" : "Crop Wiki"}
            </Link>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              {cropText(crop.name, lang)}
            </h1>
            <p className="text-sm text-gray-500 italic">{crop.scientificName}</p>
          </div>
          <Link
            to={`/crops/${crop.slug}`}
            className="text-sm font-medium text-emerald-700 hover:underline"
          >
            {isAr ? "← كيف تستخدم فارمتوبيا هذه البيانات" : "How FarmTopia uses this data →"}
          </Link>
        </div>

        {/* Reference data cards */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <WikiCard title={isAr ? "الموسم والمحصول" : "Season & crop"}>
            <WikiRow label={isAr ? "الموسم" : "Season"} value={cropText(crop.facts.season, lang)} />
            <WikiRow label={isAr ? "احتياج المياه" : "Water need"} value={cropText(crop.facts.waterNeed, lang)} />
            <WikiRow label={isAr ? "كثافة الزراعة" : "Plant density"} value={cropText(crop.facts.plantDensity, lang)} />
            <WikiRow label={isAr ? "الأصناف" : "Varieties"} value={cropText(crop.facts.varieties, lang)} />
          </WikiCard>

          <WikiCard title={isAr ? "المناخ" : "Climate"}>
            <WikiRow label={isAr ? "درجة الحرارة" : "Temperature"} value={`${wiki.climate.tempC.min}–${wiki.climate.tempC.max} °C`} />
            <WikiRow label={isAr ? "الرطوبة" : "Humidity"} value={`${wiki.climate.humidityPct.min}–${wiki.climate.humidityPct.max} %`} />
            <WikiRow label={isAr ? "ملاحظة" : "Note"} value={cropText(wiki.climate.note, lang)} />
          </WikiCard>

          <WikiCard title={isAr ? "التربة" : "Soil"}>
            <WikiRow label={isAr ? "الأس الهيدروجيني" : "pH range"} value={wiki.soil.phRange} />
            <WikiRow label={isAr ? "تحمّل الملوحة" : "Salinity tolerance"} value={`${wiki.soil.ecToleranceDSm} dS/m`} />
            <WikiRow label={isAr ? "ملاحظة" : "Note"} value={cropText(wiki.soil.note, lang)} />
          </WikiCard>

          <WikiCard title={isAr ? "المياه" : "Water"}>
            <WikiRow label={isAr ? "الأس الهيدروجيني للماء" : "Water pH"} value={wiki.water.phRange} />
            <WikiRow label={isAr ? "الاحتياج الموسمي" : "Seasonal need"} value={`${wiki.water.seasonalMm} mm`} />
            <WikiRow label={isAr ? "ملاحظة" : "Note"} value={cropText(wiki.water.note, lang)} />
          </WikiCard>

          <WikiCard title={isAr ? "التسميد (كجم/فدان)" : "Fertilizer (kg/feddan)"}>
            <WikiRow label="N" value={`${wiki.fertilizerKgPerFeddan.N.min}–${wiki.fertilizerKgPerFeddan.N.max}`} />
            <WikiRow label="P" value={`${wiki.fertilizerKgPerFeddan.P.min}–${wiki.fertilizerKgPerFeddan.P.max}`} />
            <WikiRow label="K" value={`${wiki.fertilizerKgPerFeddan.K.min}–${wiki.fertilizerKgPerFeddan.K.max}`} />
          </WikiCard>
        </div>

        {/* Phenology */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {isAr ? "مراحل النمو ومعاملات المحصول" : "Phenology & crop coefficients"}
          </h2>
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <CropPhenologyTimeline crop={crop} lang={lang} />
          </div>
        </div>

        {/* References */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {isAr ? "المراجع العلمية" : "Scientific references"}
          </h2>
          <ul className="rounded-lg border border-gray-200 bg-white p-5 space-y-2">
            {crop.references.map((ref, i) => (
              <li key={i} className="text-sm">
                <a
                  href={ref.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 hover:underline"
                >
                  {ref.name}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500">
            {isAr
              ? "بيانات المحصول مستمدة من إرشادات منظمة الأغذية والزراعة (FAO) ومصادر أبحاث الزراعة المصرية، وتُحدَّث دوريًا."
              : "Crop data is drawn from FAO guidance and Egyptian agronomy research sources, and is updated periodically."}
          </p>
        </div>
      </main>
      <Footer lang={lang} />
    </div>
  );
}
